export const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function formatWeekLabel(monday) {
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const opts = { month: 'short', day: 'numeric' };
  return `${monday.toLocaleDateString('en-US', opts)} – ${sunday.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;
}

export function calcHours(start, end, lunchMinutes = 0) {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let diff = (eh * 60 + em) - (sh * 60 + sm) - lunchMinutes;
  if (diff <= 0) diff += 24 * 60; // overnight shift
  return parseFloat((diff / 60).toFixed(2));
}

export const emptyShift = () => ({
  startTime: '', endTime: '', hours: 0, notes: '', off: false, isPTO: false,
  mileage: 0,
  otHours: 0,  // ← manual overtime hours
  lunchBreak: { enabled: false, startTime: '12:00', durationMinutes: 30 },
  shift2: null,
});

export const emptyShift2 = () => ({
  startTime: '', endTime: '', hours: 0,
  lunchBreak: { enabled: false, startTime: '12:00', durationMinutes: 30 },
});

export function buildEmptySchedule(employees) {
  const grid = {};
  employees.forEach(emp => {
    grid[emp._id] = {};
    DAYS.forEach(day => { grid[emp._id][day] = emptyShift(); });
  });
  return grid;
}

export function scheduleToGrid(schedule, employees) {
  const grid = buildEmptySchedule(employees);
  if (!schedule?.shifts) return grid;
  schedule.shifts.forEach(shift => {
    const empId = shift.employeeId?.toString?.() || shift.employeeId;
    if (grid[empId]) {
      grid[empId][shift.day] = {
        startTime:  shift.startTime  || '',
        endTime:    shift.endTime    || '',
        hours:      shift.hours      || 0,
        notes:      shift.notes      || '',
        off:        shift.off        || false,
        isPTO:      shift.isPTO      || false,
        mileage:    shift.mileage    || 0,
        otHours:    shift.otHours    || 0,  // ← restore OT on load
        lunchBreak: shift.lunchBreak || { enabled: false, startTime: '12:00', durationMinutes: 30 },
        shift2:     shift.shift2     || null,
      };
    }
  });
  return grid;
}

export function gridToShifts(grid, employees) {
  const shifts = [];
  employees.forEach(emp => {
    DAYS.forEach(day => {
      const s = grid[emp._id]?.[day];
      if (!s) return;
      const lunchMins       = s.lunchBreak?.enabled         ? (s.lunchBreak.durationMinutes       || 30) : 0;
      const shift2LunchMins = s.shift2?.lunchBreak?.enabled ? (s.shift2.lunchBreak.durationMinutes || 30) : 0;
      shifts.push({
        employeeId:   emp._id,
        employeeName: `${emp.firstName} ${emp.lastName}`,
        day,
        startTime:  s.startTime,
        endTime:    s.endTime,
        hours:      s.hours || calcHours(s.startTime, s.endTime, lunchMins),
        notes:      s.notes,
        off:        s.off,
        isPTO:      s.isPTO   || false,
        mileage:    s.mileage || 0,
        otHours:    s.otHours || 0,  // ← persist OT hours
        lunchBreak: s.lunchBreak || { enabled: false, startTime: '12:00', durationMinutes: 30 },
        shift2: s.shift2 ? {
          startTime:  s.shift2.startTime,
          endTime:    s.shift2.endTime,
          hours:      s.shift2.hours || calcHours(s.shift2.startTime, s.shift2.endTime, shift2LunchMins),
          lunchBreak: s.shift2.lunchBreak || { enabled: false, startTime: '12:00', durationMinutes: 30 },
        } : null,
      });
    });
  });
  return shifts;
}

// Total regular hours for a day (both shifts combined, excluding OT which is tracked separately)
export function totalDayHours(shift) {
  if (!shift) return 0;
  const lunchMins   = shift.lunchBreak?.enabled         ? (shift.lunchBreak.durationMinutes       || 30) : 0;
  const shift1Hours = shift.hours || calcHours(shift.startTime, shift.endTime, lunchMins);
  if (!shift.shift2) return shift1Hours;
  const shift2LunchMins = shift.shift2.lunchBreak?.enabled ? (shift.shift2.lunchBreak.durationMinutes || 30) : 0;
  const shift2Hours     = shift.shift2.hours || calcHours(shift.shift2.startTime, shift.shift2.endTime, shift2LunchMins);
  return parseFloat((shift1Hours + shift2Hours).toFixed(2));
}

// ─── Pay-period-spanning helpers ───────────────────────────────────────────────
// These build on top of the week-based helpers above without changing them —
// Schedule docs are still stored per Monday-anchored week; these just merge/split
// across however many weeks a pay period covers (1 for weekly, 2+ for longer types).

const _toISO = (d) => d.toISOString().split('T')[0];

// Every Monday whose 7-day window overlaps [periodStart, periodEnd] (both 'YYYY-MM-DD').
export function getPeriodWeekStarts(periodStart, periodEnd) {
  const start = getMonday(new Date(periodStart + 'T00:00:00'));
  const end   = new Date(periodEnd + 'T00:00:00');
  const weekStarts = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    weekStarts.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 7);
  }
  return weekStarts;
}

// Every individual calendar date in the period, each tagged with the Monday-week
// (and weekday name) it belongs to — this is the column list the grid renders.
export function getPeriodDates(periodStart, periodEnd) {
  const start = new Date(periodStart + 'T00:00:00');
  const end   = new Date(periodEnd + 'T00:00:00');
  const dates = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    const weekStart = getMonday(cursor);
    const dayIndex  = (cursor.getDay() + 6) % 7; // Mon=0 .. Sun=6, matches DAYS order
    dates.push({
      date:         new Date(cursor),
      dateISO:      _toISO(cursor),
      weekStartISO: _toISO(weekStart),
      day:          DAYS[dayIndex],
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
}

// Like scheduleToGrid, but keyed by absolute date (dateISO) instead of weekday name,
// pulling each date's shift from whichever week's Schedule doc owns it.
// `schedulesByWeekStart` is { [weekStartISO]: scheduleDocOrNull }.
export function mergeSchedulesToGrid(schedulesByWeekStart, employees, periodDates) {
  const grid = {};
  employees.forEach(emp => {
    grid[emp._id] = {};
    periodDates.forEach(pd => {
      const schedule = schedulesByWeekStart[pd.weekStartISO];
      const shift = schedule?.shifts?.find(s => {
        const empId = s.employeeId?.toString?.() || s.employeeId;
        return empId === emp._id && s.day === pd.day;
      });
      grid[emp._id][pd.dateISO] = shift ? {
        startTime:  shift.startTime  || '',
        endTime:    shift.endTime    || '',
        hours:      shift.hours      || 0,
        notes:      shift.notes      || '',
        off:        shift.off        || false,
        isPTO:      shift.isPTO      || false,
        mileage:    shift.mileage    || 0,
        otHours:    shift.otHours    || 0,
        lunchBreak: shift.lunchBreak || { enabled: false, startTime: '12:00', durationMinutes: 30 },
        shift2:     shift.shift2     || null,
      } : emptyShift();
    });
  });
  return grid;
}

// Inverse of mergeSchedulesToGrid — splits a date-keyed grid back into one
// shifts[] array per weekStartISO, in the same shape gridToShifts produces,
// so each week can be saved with the existing POST /api/schedules endpoint.
export function gridToShiftsByWeek(grid, employees, periodDates) {
  const shiftsByWeek = {};
  periodDates.forEach(pd => {
    if (!shiftsByWeek[pd.weekStartISO]) shiftsByWeek[pd.weekStartISO] = [];
  });

  employees.forEach(emp => {
    periodDates.forEach(pd => {
      const s = grid[emp._id]?.[pd.dateISO];
      if (!s) return;
      const lunchMins       = s.lunchBreak?.enabled         ? (s.lunchBreak.durationMinutes       || 30) : 0;
      const shift2LunchMins = s.shift2?.lunchBreak?.enabled ? (s.shift2.lunchBreak.durationMinutes || 30) : 0;
      shiftsByWeek[pd.weekStartISO].push({
        employeeId:   emp._id,
        employeeName: `${emp.firstName} ${emp.lastName}`,
        day:        pd.day,
        startTime:  s.startTime,
        endTime:    s.endTime,
        hours:      s.hours || calcHours(s.startTime, s.endTime, lunchMins),
        notes:      s.notes,
        off:        s.off,
        isPTO:      s.isPTO   || false,
        mileage:    s.mileage || 0,
        otHours:    s.otHours || 0,
        lunchBreak: s.lunchBreak || { enabled: false, startTime: '12:00', durationMinutes: 30 },
        shift2: s.shift2 ? {
          startTime:  s.shift2.startTime,
          endTime:    s.shift2.endTime,
          hours:      s.shift2.hours || calcHours(s.shift2.startTime, s.shift2.endTime, shift2LunchMins),
          lunchBreak: s.shift2.lunchBreak || { enabled: false, startTime: '12:00', durationMinutes: 30 },
        } : null,
      });
    });
  });

  return shiftsByWeek;
}

export const toISODate = _toISO;

// Replicates a single week's weekday-keyed pattern (e.g. from a template or
// default Schedule doc, shaped { Monday: shift, Tuesday: shift, ... }) across
// every date in a period — used when loading a template/default onto a
// multi-week pay-period grid so the recurring pattern repeats each week.
export function applyWeekdayPatternAcrossPeriod(weekdayGridForEmp, periodDates) {
  const result = {};
  periodDates.forEach(pd => {
    result[pd.dateISO] = weekdayGridForEmp?.[pd.day] || emptyShift();
  });
  return result;
}

// Label for the currently displayed period's date range, e.g. "Mar 16 – Mar 29, 2026".
// Works for both a single week (7 entries) and a longer pay period.
export function formatPeriodLabel(periodDates) {
  if (!periodDates?.length) return '';
  const first = periodDates[0].date;
  const last  = periodDates[periodDates.length - 1].date;
  const opts  = { month: 'short', day: 'numeric' };
  return `${first.toLocaleDateString('en-US', opts)} – ${last.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;
}